import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, Phone } from "lucide-react";
import { useState } from "react";
import { useStore } from "@/context/StoreContext";
import { fileUrl } from "@/api";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const NAV_LINKS = [
  { to: "/catalogo/packs", label: "Packs" },
  { to: "/catalogo/ramos", label: "Ramos" },
  { to: "/catalogo/arreglos", label: "Arreglos" },
  { to: "/cotizacion", label: "Cotización personalizada" },
];

export default function Navbar() {
  const { settings, cartTotals, setCartOpen } = useStore();
  const [mobile, setMobile] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 backdrop-blur-md border-b"
      style={{
        background: "color-mix(in srgb, var(--brand-background) 85%, transparent)",
        borderColor: "rgba(0,0,0,0.06)",
      }}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
          {settings?.logo_url ? (
            <img
              src={fileUrl(settings.logo_url)}
              alt="Floral Rossé"
              className="h-14 w-14 object-contain"
            />
          ) : (
            <div className="h-14 w-14 rounded-full bg-[var(--brand-primary)]" />
          )}
          <div className="hidden sm:block">
            <div className="font-serif text-2xl font-bold leading-none">
              {settings?.business_name || "Floral Rossé"}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-1">
              Flores artificiales premium
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm font-semibold hover:text-[var(--brand-primary)] transition-colors"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {settings?.whatsapp_number && (
            <a
              href={`https://wa.me/${settings.whatsapp_number.replace(/\D/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="hidden md:inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-[var(--brand-accent)] text-[var(--brand-accent)] hover:bg-[var(--brand-accent)] hover:text-white transition-colors"
              data-testid="nav-whatsapp-link"
            >
              <Phone size={16} /> WhatsApp
            </a>
          )}
          <Button
            onClick={() => setCartOpen(true)}
            className="btn-primary rounded-full h-11 px-5 relative"
            data-testid="nav-cart-button"
          >
            <ShoppingBag size={18} className="mr-2" />
            Carrito
            {cartTotals.items > 0 && (
              <span
                className="absolute -top-1 -right-1 bg-[var(--brand-accent)] text-white text-[11px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center"
                data-testid="nav-cart-count"
              >
                {cartTotals.items}
              </span>
            )}
          </Button>
          <Sheet open={mobile} onOpenChange={setMobile}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className="lg:hidden h-11 w-11 p-0 rounded-full"
                data-testid="nav-mobile-trigger"
              >
                <Menu size={20} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[var(--brand-background)]">
              <SheetHeader>
                <SheetTitle className="font-serif text-2xl">Menú</SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-2 mt-6">
                {NAV_LINKS.map((l) => (
                  <button
                    key={l.to}
                    onClick={() => {
                      setMobile(false);
                      navigate(l.to);
                    }}
                    className="text-left py-3 px-4 rounded-xl hover:bg-white font-semibold"
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
